import { useCallback, useEffect, useRef, useState } from 'react'
import ResizeObserver from 'resize-observer-polyfill'

export function useParentOffset() {
  const [parentRect, setParentRect] = useState<DOMRect | null>(null)
  const [childRect, setChildRect] = useState<DOMRect | null>(null)
  const [offset, setOffset] = useState<{ left: number; top: number; width: number; height: number } | null>(null)
  const [childNode, setChildNode] = useState<HTMLElement>()
  const [parentNode, setParentNode] = useState<HTMLElement>()
  const childNodeRef = useRef<HTMLElement>()
  const parentNodeRef = useRef<HTMLElement>()

  const resizeObserverRef = useRef<ResizeObserver>()

  useEffect(() => {
    if (resizeObserverRef.current) {
      return
    }
    // TODO we must create only one ResizeObserver instance for entire application
    const observer = new ResizeObserver(() => {
      if (parentNodeRef.current) {
        const node = parentNodeRef.current
        const rect = node.getBoundingClientRect()
        const borderWidth = parseInt(getComputedStyle(node).getPropertyValue('border-left-width'), 10) || 0
        setParentRect({
          ...rect,
          left: rect.left + borderWidth,
        })
      }
      if (childNodeRef.current) {
        setChildRect(childNodeRef.current.getBoundingClientRect())
      }
    })
    resizeObserverRef.current = observer

    return () => {
      observer.disconnect()
    }
  }, [])

  const parentRef = useCallback(node => {
    if (node !== null) {
      const rect = node.getBoundingClientRect()
      const borderWidth = parseInt(getComputedStyle(node).getPropertyValue('border-left-width'), 10) || 0
      setParentRect({
        ...rect,
        left: rect.left + borderWidth,
      })
      setParentNode(node)
      parentNodeRef.current = node
    }
  }, [])

  const childRef = useCallback(node => {
    if (node !== null) {
      setChildRect(node.getBoundingClientRect())
      setChildNode(node)
      childNodeRef.current = node
    }
  }, [])

  useEffect(() => {
    const cnode = childNode
    const pnode = parentNode
    const observer = resizeObserverRef.current
    if (cnode && pnode && observer) {
      observer.observe(cnode)
      observer.observe(pnode)
      return () => {
        observer.unobserve(cnode)
        observer.unobserve(pnode)
      }
    }
  }, [childNode, parentNode])

  useEffect(() => {
    if (parentRect && childRect) {
      setOffset({
        left: childRect.left - parentRect.left,
        top: childRect.top - parentRect.top,
        width: childRect.width,
        height: childRect.height,
      })
    } else {
      setOffset(null)
    }
  }, [parentRect, childRect])

  return [parentRef, childRef, offset] as const
}
